import { cpFetch, createAuthHeaders } from '../../lib/cpClient';
import { useAuthStore } from '../../store/authStore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export interface EntityMemory {
  id: string;
  tenant_id: string;
  entity_id: string;
  entity_type: string;
  memory_type: 'decision' | 'observation' | 'conversation' | 'recommendation' | 'event';
  content: {
    title: string;
    detail?: string;
    structured?: Record<string, unknown>;
  };
  related_entities?: Array<{ entity_id: string; entity_type: string; role?: string }>;
  conversation?: {
    session_id?: string;
    channel?: string;
    summary?: string;
    key_exchanges?: Array<{ role: string; content: string }>;
    turn_count?: number;
    agent_id?: string;
  };
  timestamp: string;
  season?: string;
  tags?: string[];
  source?: {
    type: 'system' | 'conversation';
    agent_id?: string;
    interaction_id?: string;
    confidence?: number;
  };
  outcome?: {
    status: string;
    result?: string;
    evaluated_at?: string;
    notes?: string;
  };
  created_at: string;
  updated_at: string;
}

export interface MemoryFilters {
  memory_types?: string[];
  tags?: string[];
  time_from?: string;
  time_to?: string;
  season?: string;
  max_results?: number;
}

export interface SearchFilters {
  entity_types?: string[];
  memory_types?: string[];
  from?: string;
  to?: string;
}

interface CreateMemoryPayload {
  entity_id: string;
  entity_type: string;
  memory_type: EntityMemory['memory_type'];
  content: EntityMemory['content'];
  related_entities?: EntityMemory['related_entities'];
  conversation?: EntityMemory['conversation'];
  timestamp?: string;
  season?: string;
  tags?: string[];
  source?: EntityMemory['source'];
}

interface UpdateOutcomePayload {
  memoryId: string;
  outcome: NonNullable<EntityMemory['outcome']>;
}

// API client functions
export const entityMemoriesApi = {
  getMemories: async (
    entityId: string,
    filters: MemoryFilters | undefined,
    token: string,
    tenant: string
  ): Promise<{ memories: EntityMemory[] }> => {
    const params = new URLSearchParams();
    if (filters?.memory_types?.length) params.set('memory_types', filters.memory_types.join(','));
    if (filters?.tags?.length) params.set('tags', filters.tags.join(','));
    if (filters?.time_from) params.set('time_from', filters.time_from);
    if (filters?.time_to) params.set('time_to', filters.time_to);
    if (filters?.season) params.set('season', filters.season);
    if (filters?.max_results) params.set('max_results', filters.max_results.toString());

    const qs = params.toString();
    const url = `entity-memories/${encodeURIComponent(entityId)}${qs ? `?${qs}` : ''}`;

    return cpFetch(url, {
      headers: createAuthHeaders(token, tenant),
    });
  },

  getTimeline: async (
    entityId: string,
    granularity: 'day' | 'week' | 'month' | undefined,
    token: string,
    tenant: string
  ) => {
    const params = new URLSearchParams();
    if (granularity) params.set('granularity', granularity);

    const qs = params.toString();
    const url = `entity-memories/${encodeURIComponent(entityId)}/timeline${qs ? `?${qs}` : ''}`;

    return cpFetch(url, {
      headers: createAuthHeaders(token, tenant),
    });
  },

  searchMemories: async (
    query: string,
    filters: SearchFilters | undefined,
    token: string,
    tenant: string
  ): Promise<{ results: EntityMemory[] }> => {
    const params = new URLSearchParams({ q: query });
    if (filters?.entity_types?.length) params.set('entity_types', filters.entity_types.join(','));
    if (filters?.memory_types?.length) params.set('memory_types', filters.memory_types.join(','));
    if (filters?.from) params.set('from', filters.from);
    if (filters?.to) params.set('to', filters.to);

    return cpFetch(`entity-memories/search?${params}`, {
      headers: createAuthHeaders(token, tenant),
    });
  },

  createMemory: async (
    payload: CreateMemoryPayload,
    token: string,
    tenant: string
  ): Promise<EntityMemory> => {
    return cpFetch('entity-memories', {
      method: 'POST',
      headers: {
        ...createAuthHeaders(token, tenant),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  },

  updateOutcome: async (
    memoryId: string,
    outcome: NonNullable<EntityMemory['outcome']>,
    token: string,
    tenant: string
  ): Promise<EntityMemory> => {
    return cpFetch(`entity-memories/${encodeURIComponent(memoryId)}/outcome`, {
      method: 'PUT',
      headers: {
        ...createAuthHeaders(token, tenant),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(outcome),
    });
  },
};

// React Query hooks

export function useEntityMemories(entityId: string, filters?: MemoryFilters) {
  const { token, selectedTenant } = useAuthStore();

  return useQuery({
    queryKey: ['entity-memories', entityId, filters, { tid: selectedTenant }],
    queryFn: () => {
      if (!token) throw new Error('No authentication token');
      const tenant = selectedTenant || 'dev';
      return entityMemoriesApi.getMemories(entityId, filters, token, tenant);
    },
    enabled: !!token && !!entityId,
    staleTime: 5000,
  });
}

export function useEntityTimeline(entityId: string, granularity?: 'day' | 'week' | 'month') {
  const { token, selectedTenant } = useAuthStore();

  return useQuery({
    queryKey: ['entity-memories', 'timeline', entityId, granularity, { tid: selectedTenant }],
    queryFn: () => {
      if (!token) throw new Error('No authentication token');
      const tenant = selectedTenant || 'dev';
      return entityMemoriesApi.getTimeline(entityId, granularity, token, tenant);
    },
    enabled: !!token && !!entityId,
    staleTime: 5000,
  });
}

export function useSearchMemories(query: string, filters?: SearchFilters) {
  const { token, selectedTenant } = useAuthStore();

  return useQuery({
    queryKey: ['entity-memories', 'search', query, filters, { tid: selectedTenant }],
    queryFn: () => {
      if (!token) throw new Error('No authentication token');
      const tenant = selectedTenant || 'dev';
      return entityMemoriesApi.searchMemories(query, filters, token, tenant);
    },
    enabled: !!token && !!query,
    staleTime: 5000,
  });
}

export function useCreateMemory() {
  const { token, selectedTenant } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['entity-memories', 'create'],
    mutationFn: async (payload: CreateMemoryPayload) => {
      if (!token || !selectedTenant) {
        throw new Error('No authentication token or tenant');
      }
      return entityMemoriesApi.createMemory(payload, token, selectedTenant);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entity-memories', variables.entity_id] });
      queryClient.invalidateQueries({ queryKey: ['entity-memories', 'search'] });
    },
  });
}

export function useUpdateMemoryOutcome() {
  const { token, selectedTenant } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['entity-memories', 'update-outcome'],
    mutationFn: async ({ memoryId, outcome }: UpdateOutcomePayload) => {
      if (!token || !selectedTenant) {
        throw new Error('No authentication token or tenant');
      }
      return entityMemoriesApi.updateOutcome(memoryId, outcome, token, selectedTenant);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entity-memories'] });
    },
  });
}
